/**
 * IAM（EnderRealm OIDC Provider）授权登录：标准授权码流程 + PKCE（RFC 7636）。
 * console 是公开客户端（public client），不持有 client_secret；
 * 令牌交换直接在浏览器完成，身份信息取自 userinfo 接口的 OIDC Standard Claims。
 * 各端点地址通过 discovery 动态发现，不硬编码路径，IAM 侧路由调整不受影响。
 */

const ISSUER = process.env.ER_IAM_ISSUER ?? 'http://localhost:5173/oidc';
const CLIENT_ID = 'enderrealm-console';
const SCOPES = 'openid profile email';
const REDIRECT_URI = `${window.location.origin}/callback`;

const CLAIMS_KEY = 'er_iam_claims';
const OAUTH_STATE_KEY = 'er_iam_oauth_state';
const OAUTH_VERIFIER_KEY = 'er_iam_oauth_verifier';

/** discovery 文档中 console 用到的端点 */
interface OidcEndpoints {
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  end_session_endpoint: string;
}

const DISCOVERY_URL = `${ISSUER}/.well-known/openid-configuration`;
const OAUTH_ENDPOINTS_KEY = 'er_iam_oauth_endpoints';

async function fetchEndpoints(): Promise<OidcEndpoints> {
  const res = await fetch(DISCOVERY_URL);
  if (!res.ok) throw new Error(`获取 OIDC 配置失败（${res.status}）`);
  const doc = (await res.json()) as Partial<Record<keyof OidcEndpoints, unknown>>;
  for (const key of ['authorization_endpoint', 'token_endpoint', 'userinfo_endpoint', 'end_session_endpoint'] as const) {
    if (typeof doc[key] !== 'string') {
      throw new Error(`OIDC 配置缺少 ${key}`);
    }
  }
  return doc as OidcEndpoints;
}

/** 构建期注入的 ISSUER 异常时给出可读报错（常见：少了冒号的 https//、重复协议头、带引号/空格） */
function assertIssuerValid(): void {
  if (!/^https?:\/\/[^\s"'=]+$/.test(ISSUER)) {
    throw new Error(
      `ER_IAM_ISSUER 配置不合法：「${ISSUER}」——应为形如 https://iam-api.enderrealm.cn/oidc 的完整地址`,
    );
  }
}

/** 端点随授权流程缓存在 sessionStorage（跨 IAM 跳转复用），缺失时重新发现 */
async function loadEndpoints(): Promise<OidcEndpoints> {
  assertIssuerValid();
  try {
    const raw = sessionStorage.getItem(OAUTH_ENDPOINTS_KEY);
    if (raw) {
      const cached = JSON.parse(raw) as Partial<OidcEndpoints>;
      if (
        cached.authorization_endpoint &&
        cached.token_endpoint &&
        cached.userinfo_endpoint &&
        cached.end_session_endpoint
      ) {
        return cached as OidcEndpoints;
      }
    }
  } catch {
    // 缓存损坏则重新发现
  }
  const endpoints = await fetchEndpoints();
  sessionStorage.setItem(OAUTH_ENDPOINTS_KEY, JSON.stringify(endpoints));
  return endpoints;
}

/** 会话临近过期提前重新授权（IAM 侧有 SSO 会话时通常静默完成） */
const EXPIRY_MARGIN_MS = 60_000;

export interface IamClaims {
  sub: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  exp?: number;
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64url(new Uint8Array(digest)) };
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split('.')[1];
  if (!part) throw new Error('id_token 格式不正确');
  const bytes = Uint8Array.from(atob(part.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
    c.charCodeAt(0),
  );
  return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
}

function toClaims(payload: Record<string, unknown>): IamClaims {
  if (typeof payload.sub !== 'string' || payload.sub.length === 0) {
    throw new Error('id_token 缺少 sub 声明');
  }
  return {
    sub: payload.sub,
    name: typeof payload.name === 'string' ? payload.name : undefined,
    preferred_username:
      typeof payload.preferred_username === 'string' ? payload.preferred_username : undefined,
    email: typeof payload.email === 'string' ? payload.email : undefined,
    exp: typeof payload.exp === 'number' ? payload.exp : undefined,
  };
}

function buildAuthorizeUrl(endpoints: OidcEndpoints, state: string, challenge: string): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: SCOPES,
    state,
    nonce: base64url(crypto.getRandomValues(new Uint8Array(16))),
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  return `${endpoints.authorization_endpoint}?${params}`;
}

function loadStoredClaims(): IamClaims | null {
  try {
    const raw = localStorage.getItem(CLAIMS_KEY);
    if (!raw) return null;
    const claims = JSON.parse(raw) as IamClaims;
    if (!claims.sub) return null;
    if (claims.exp && claims.exp * 1000 < Date.now() + EXPIRY_MARGIN_MS) return null;
    return claims;
  } catch {
    return null;
  }
}

/** 发起授权跳转（携带 state + PKCE） */
export async function redirectToAuthorize(): Promise<void> {
  const endpoints = await loadEndpoints();
  const state = base64url(crypto.getRandomValues(new Uint8Array(24)));
  const { verifier, challenge } = await createPkcePair();
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(OAUTH_VERIFIER_KEY, verifier);
  window.location.assign(buildAuthorizeUrl(endpoints, state, challenge));
}

/** 授权回调：校验 state 后用授权码换取令牌；身份信息按 OIDC 规范取自 userinfo */
export async function finishCallbackLogin(): Promise<IamClaims> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (error) throw new Error(params.get('error_description') ?? error);

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(OAUTH_VERIFIER_KEY);
  if (!code || !state || !expectedState || state !== expectedState || !verifier) {
    throw new Error('回调参数校验失败，请重新登录');
  }

  const endpoints = await loadEndpoints();

  const res = await fetch(endpoints.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    }),
  });
  if (!res.ok) throw new Error(`令牌交换失败（${res.status}）`);
  const tokens = (await res.json()) as { id_token?: string; access_token?: string };
  if (!tokens.id_token || !tokens.access_token) {
    throw new Error('令牌响应缺少 id_token 或 access_token');
  }

  // 授权码流下 ID Token 仅含 sub；profile/email 等 claims 经由 userinfo 返回（OIDC Core 5.4）
  const base = toClaims(decodeJwtPayload(tokens.id_token));
  const infoRes = await fetch(endpoints.userinfo_endpoint, {
    headers: { Authorization: `Bearer ${tokens.access_token}` },
  });
  if (!infoRes.ok) throw new Error(`获取用户信息失败（${infoRes.status}）`);
  const info = (await infoRes.json()) as Record<string, unknown>;

  const claims: IamClaims = {
    sub: base.sub,
    exp: base.exp,
    name: typeof info.name === 'string' ? info.name : undefined,
    preferred_username:
      typeof info.preferred_username === 'string' ? info.preferred_username : undefined,
    email: typeof info.email === 'string' ? info.email : undefined,
  };
  localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims));
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_VERIFIER_KEY);
  return claims;
}

/** 读取本地已存的 IAM 会话（不发起任何跳转）；过期/不存在返回 null */
export function loadIamClaims(): IamClaims | null {
  return loadStoredClaims();
}

/** 退出登录：清除本地会话并跳转 IAM end-session（会询问确认后回到本站） */
export async function iamLogout(): Promise<void> {
  localStorage.removeItem(CLAIMS_KEY);
  const { end_session_endpoint } = await loadEndpoints();
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    post_logout_redirect_uri: window.location.origin,
  });
  window.location.assign(`${end_session_endpoint}?${params}`);
}
